import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import { getUserRole, UserRole } from '../../utils/user';
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { callQuery, SystemBanner, TrackboardColumn, USER_SETTINGS, UserSettings } from '../../amplify/appsync/query';
import { callWrite, UPDATE_USER_SETTINGS } from '../../amplify/appsync/write';

export const VALID_USER_AGREEMENT_VERSION = "1.0.2";

type GlobalOptionsContextType = {
    isPresentation: boolean;
    toggleIsPresentation: () => void;
    getDerivedRole: (user: UserRole) => string;
    getTrackboardColumns: (zone?: string) => TrackboardColumn[] | undefined;
    getTrackboardColumnsByZone: () => Record<string, TrackboardColumn[]> | undefined;
    setTrackboardColumnsGlobal: (columns: TrackboardColumn[] | undefined) => void;
    setTrackboardColumnsByZone: (columnsByZone: Record<string, TrackboardColumn[]> | undefined) => void;
    hasValidUserAgreement: () => boolean;
    markUserAgreementValid: (agreement: string) => void;
    changeAssumedRole: (user: UserRole, assumedRole: string) => void;
    changePageContext: (pageContext: any) => void;
    getPageContext: () => any;
    getSystemBanner: () => SystemBanner | null;
    loadingSettings: boolean;
};

const GlobalOptionsContext = createContext<GlobalOptionsContextType | undefined>(undefined);

const DEFAULT_TRACKBOARD_ZONES = [
    "All",
    "Lobby",
    "Alpha",
    "Bravo",
    "Cardinal",
    "Delta",
    "Emerald",
    "Foxtrot",
    "Indigo",
    "Jade",
    "COMPASS",
    "Waiting",
];

const buildColumnsForZone = (columns: TrackboardColumn[], zone: string) => {
    const normalized = columns.map(col => ({
        id: col.id,
        show: col.show ?? true,
    }));

    const withoutSuitability = normalized.filter(col => col.id !== "suitability");

    if (zone === "Lobby") {
        withoutSuitability.push({
            id: "suitability",
            show: true,
        });
    }

    return withoutSuitability;
};

const seedColumnsByZone = (columns: TrackboardColumn[]) => {
    const zones = Array.from(new Set(["All", ...DEFAULT_TRACKBOARD_ZONES]));
    return zones.reduce<Record<string, TrackboardColumn[]>>((acc, zone) => {
        acc[zone] = buildColumnsForZone(columns, zone);
        return acc;
    }, {});
};

export const resolveTrackboardColumns = (
    zone: string | undefined,
    global: TrackboardColumn[] | undefined,
    byZone: Record<string, TrackboardColumn[]> | undefined,
) => {
    if (zone) {
        if (byZone?.[zone]) {
            return byZone[zone];
        }
        if (byZone?.All) {
            return byZone.All;
        }
    }
    return global ?? byZone?.All;
};

export const GlobalOptionsProvider = ({ children }: { children: ReactNode }) => {
    const [isPresentation, setIsPresentation] = useState(false);
    const [assumedRole, setAssumedRole] = useState<string>("");
    const [trackboardColumnsGlobal, setTrackboardColumnsGlobal] = useState<TrackboardColumn[] | undefined>();
    const [trackboardColumnsByZone, setTrackboardColumnsByZone] = useState<Record<string, TrackboardColumn[]> | undefined>();
    const [userAgreementVersion, setUserAgreementVersion] = useState<string | undefined>();
    const [systemBanner, setSystemBanner] = useState<SystemBanner | null>(null);
    const [pageContext, setPageContext] = useState({});
    const [loadingSettings, setLoadingSettings] = useState(true);

    // Fetch user settings on mount
    useEffect(() => {
        let mounted = true;

        const fetchSettings = async () => {
            try {
                const settings: UserSettings = await callQuery({
                    operation: USER_SETTINGS,
                });

                // console.log("fetchSettings", settings);

                if (mounted && settings) {
                    setIsPresentation(!!settings.is_presentation);
                    setAssumedRole(settings.assumed_role ?? "");
                    const globalColumns = settings.trackboard_columns ?? undefined;
                    const byZoneColumns = settings.trackboard_columns_by_zone ?? undefined;

                    setTrackboardColumnsGlobal(globalColumns);
                    if (byZoneColumns) {
                        setTrackboardColumnsByZone(byZoneColumns);
                    } else if (globalColumns) {
                        setTrackboardColumnsByZone(seedColumnsByZone(globalColumns));
                    } else {
                        setTrackboardColumnsByZone(undefined);
                    }
                    setUserAgreementVersion(settings.user_agreement_version ?? undefined);
                    setSystemBanner(settings.system_banner ?? null);
                }
            } catch (err) {
                console.error("Error loading user settings", err);
            } finally {
                if (mounted) setLoadingSettings(false);
            }
        };

        fetchSettings();

        return () => {
            mounted = false;
        };
    }, []);

    const isProd = () => {
        return !(window.location.hostname.startsWith("d1sucbtpe2zggt") || window.location.hostname.startsWith("localhost"));
    };

    const toggleIsPresentation = useCallback(() => {
        setIsPresentation(prev => !prev);

        callWrite({
            operation: UPDATE_USER_SETTINGS,
            settings: { is_presentation: !isPresentation }
        }).catch((err: any) => {
            console.error("Failed to update isPresentation", err);
        });
    }, [isPresentation]);

    const changeAssumedRole = useCallback((user: UserRole, newAssumedRole: string) => {
        if (getUserRole(user) === UserRole.ADMIN) {
            setAssumedRole(newAssumedRole);

            callWrite({
                operation: UPDATE_USER_SETTINGS,
                settings: { assumed_role: newAssumedRole }
            }).catch((err: any) => {
                console.error("Failed to update assumedRole", err);
            });
        }
    }, []);

    const getDerivedRole = useCallback((user: UserRole) => {
        if (getUserRole(user) !== UserRole.ADMIN || !assumedRole) {
            return getUserRole(user);
        } else {
            return assumedRole;
        }
    }, [assumedRole]);

    const hasValidUserAgreement = useCallback(() => {
        if (loadingSettings) {
            // Avoid showing modal when settings are still loading
            return true;
        }
        if (!userAgreementVersion) {
            return false;
        }
        return userAgreementVersion === VALID_USER_AGREEMENT_VERSION;
    }, [loadingSettings, userAgreementVersion]);

    const markUserAgreementValid = useCallback((userAgreementVersion: string) => {
        setUserAgreementVersion(userAgreementVersion);
    }, []);

    const getTrackboardColumns = useCallback((zone?: string) => {
        return resolveTrackboardColumns(zone, trackboardColumnsGlobal, trackboardColumnsByZone);
    }, [trackboardColumnsByZone, trackboardColumnsGlobal]);

    const getTrackboardColumnsByZone = useCallback(() => {
        return trackboardColumnsByZone;
    }, [trackboardColumnsByZone]);

    const handleSetTrackboardColumnsGlobal = useCallback((columns: TrackboardColumn[] | undefined) => {
        setTrackboardColumnsGlobal(columns);
    }, []);

    const handleSetTrackboardColumnsByZone = useCallback((columnsByZone: Record<string, TrackboardColumn[]> | undefined) => {
        setTrackboardColumnsByZone(columnsByZone);
    }, []);

    const changePageContext = useCallback((ctx: any) => {
        setPageContext(ctx);
    }, []);

    const getPageContext = useCallback(() => pageContext, [pageContext]);

    const getSystemBanner = useCallback(() => systemBanner, [systemBanner]);

    return (
        <GlobalOptionsContext.Provider
            value={{
                isPresentation,
                toggleIsPresentation,
                changeAssumedRole,
                getDerivedRole,
                getTrackboardColumns,
                getTrackboardColumnsByZone,
                setTrackboardColumnsGlobal: handleSetTrackboardColumnsGlobal,
                setTrackboardColumnsByZone: handleSetTrackboardColumnsByZone,
                hasValidUserAgreement,
                markUserAgreementValid,
                changePageContext,
                getPageContext,
                getSystemBanner,
                loadingSettings
            }}
        >
            {children}
        </GlobalOptionsContext.Provider>
    );
};

export const useGlobalOptions = () => {
    const context = useContext(GlobalOptionsContext);
    if (context === undefined) {
        throw new Error('useGlobalOptions must be used within a GlobalOptionsContext');
    }
    return context;
};

export default function useSetPagePatientVisit(patientVisit?: PatientVisit) {
    const { changePageContext } = useGlobalOptions();

    useEffect(() => {
        changePageContext({ patient_visit: patientVisit });
        return () => {
            changePageContext({ patient_visit: null });
        };
    }, [patientVisit, changePageContext]);
}
